import {
  Input,
  Label,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  Typography,
} from "@/components/ui";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS } from "@/lib/data/static/contactStatics";
import {
  MEDICAL_TREATMENT_LETTER_OF_PROTECTION_STATUS_OPTIONS,
  MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS,
  MEDICAL_TREATMENT_STATUS_OPTIONS,
} from "@/lib/data/static/matterStatics";
import type { MedicalTreatment } from "@/lib/types";

export type MedicalTreatmentDetailsDraft = {
  type: MedicalTreatment["type"];
  status: string;
  patientAccountNumber: string;
  portalLink: string;
  portalDetails: string;
  description: string;
  letterOfProtection: "" | NonNullable<MedicalTreatment["letterOfProtection"]>;
  lien: "" | NonNullable<MedicalTreatment["lien"]>;
  billNotApplicable: boolean;
  recordNotApplicable: boolean;
  planNotApplicable: boolean;
};

interface MedicalTreatmentDetailsFieldsProps {
  values: MedicalTreatmentDetailsDraft;
  onChange: (updates: Partial<MedicalTreatmentDetailsDraft>) => void;
  disabled: boolean;
  hasBills: boolean;
  hasRecords: boolean;
  hasPlans: boolean;
}

export function MedicalTreatmentDetailsFields({
  values,
  onChange,
  disabled,
  hasBills,
  hasRecords,
  hasPlans,
}: MedicalTreatmentDetailsFieldsProps) {
  return (
    <fieldset disabled={disabled}>
      <div className="space-y-4 px-3 pb-6">
        <div>
          <h3 className="text-sm font-semibold">Edit treatment info</h3>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Type</Label>
            <MultiSelect
              disabled={disabled}
              options={CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS.map(
                (type) => ({ label: type, value: type }),
              )}
              selectedValues={values.type}
              setSelectedValues={(value) => {
                onChange({
                  type: value as MedicalTreatmentDetailsDraft["type"],
                });
              }}
              onValueChange={(value) => {
                onChange({
                  type: value as MedicalTreatmentDetailsDraft["type"],
                });
              }}
              placeholder="Select treatment types"
              maxCount={1}
              modalPopover
            />
          </div>
          <div className="space-y-1.5">
            <Label>Treatment status</Label>
            <Select
              disabled={disabled}
              value={values.status || "none"}
              onValueChange={(value) => {
                onChange({ status: value === "none" ? "" : value });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">-</SelectItem>
                {MEDICAL_TREATMENT_STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Account number</Label>
            <Input
              value={values.patientAccountNumber}
              onChange={(event) => {
                onChange({ patientAccountNumber: event.target.value });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Portal link</Label>
            <Input
              type="url"
              placeholder="https://"
              value={values.portalLink}
              onChange={(event) => {
                onChange({ portalLink: event.target.value });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Letter of protection</Label>
            <Select
              disabled={disabled}
              value={values.letterOfProtection || "none"}
              onValueChange={(value) => {
                onChange({
                  letterOfProtection:
                    value === "none"
                      ? ""
                      : (value as MedicalTreatmentDetailsDraft["letterOfProtection"]),
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select letter of protection" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">-</SelectItem>
                {MEDICAL_TREATMENT_LETTER_OF_PROTECTION_STATUS_OPTIONS.map(
                  (status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Lien</Label>
            <Select
              disabled={disabled}
              value={values.lien || "none"}
              onValueChange={(value) => {
                onChange({
                  lien:
                    value === "none"
                      ? ""
                      : (value as MedicalTreatmentDetailsDraft["lien"]),
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">-</SelectItem>
                {MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Description</Label>
            <Textarea
              value={values.description}
              onChange={(event) => {
                onChange({ description: event.target.value });
              }}
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Portal details</Label>
            <Textarea
              value={values.portalDetails}
              onChange={(event) => {
                onChange({ portalDetails: event.target.value });
              }}
            />
          </div>
          <div className="space-y-4 md:col-span-2">
            <div className="border-border border-t pt-6">
              <h3 className="text-sm font-semibold">Settings</h3>
            </div>
            <TooltipWrapper
              message={
                hasBills
                  ? "Cannot enable if bills exist for this treatment."
                  : undefined
              }
            >
              <div className="border-border flex items-center gap-3 rounded-md border px-3 py-3">
                <Switch
                  checked={values.billNotApplicable}
                  onCheckedChange={(value) => {
                    onChange({ billNotApplicable: value });
                  }}
                  disabled={disabled || hasBills}
                />
                <Typography variant="subHeading" size="sm">
                  Billing records not applicable
                </Typography>
              </div>
            </TooltipWrapper>
            <TooltipWrapper
              message={
                hasRecords
                  ? "Cannot enable if records exist for this treatment."
                  : undefined
              }
            >
              <div className="border-border flex items-center gap-3 rounded-md border px-3 py-3">
                <Switch
                  checked={values.recordNotApplicable}
                  onCheckedChange={(value) => {
                    onChange({ recordNotApplicable: value });
                  }}
                  disabled={disabled || hasRecords}
                />
                <Typography variant="subHeading" size="sm">
                  Medical records not applicable
                </Typography>
              </div>
            </TooltipWrapper>
            <TooltipWrapper
              message={
                hasPlans
                  ? "Cannot enable if plans exist for this treatment."
                  : undefined
              }
            >
              <div className="border-border flex items-center gap-3 rounded-md border px-3 py-3">
                <Switch
                  checked={values.planNotApplicable}
                  onCheckedChange={(value) => {
                    onChange({ planNotApplicable: value });
                  }}
                  disabled={disabled || hasPlans}
                />
                <Typography variant="subHeading" size="sm">
                  Treatment plan not applicable
                </Typography>
              </div>
            </TooltipWrapper>
          </div>
        </div>
      </div>
    </fieldset>
  );
}
